import BottomSheet from '../../bottom-sheet/BottomSheet';
import logger from '../../../utils/logger';
import UpdateForm from '../forms/UpdateForm';
import { isMobileDevice } from '../../../utils/breakpoints';
import { LineBreak } from '../MyOrdersTableWrapperStyle';
import { messageContents } from '../../../constants/message';
import { openMessageModal } from '../../../utils/message';
import { RENTAL_NOTES_TYPE_ID } from '../../../constants/rentals';
import { RootState, useAppSelector } from '../../../redux/store';
import { scheduleOffRentOrderUpdateSchema } from '../../../types/order-forms';
import { UpdateModal } from './UpdateModal';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import {
  cancelScheduleOffrent,
  offRentOrderNotesUpdate,
  offRentOrder,
} from '../../../apis/order';
import {
  OffRentOrderData,
  OffRentOrderDataUpdateNotes,
  ScheduleOffRentOrderDetails,
  OffRentOrderUpdateFormFieldType,
} from '../../../types/offrent-types';
import {
  BottomsheetContentWrapper,
  BottomsheetEquipmentContentWrapper,
  BottomSheetLabel,
  BottomSheetTopBar,
  CloseButton,
  CloseIcon,
  EquipmentDetails,
  EquipmentDetailsHeader,
  EquipmentDetailsSubHeader,
  UpdateFormContainer,
} from '../forms/Style';
import {
  OrdersModalVisibility,
  FieldDetail,
  InputFieldComponentType,
  OrderDisplayModalType,
  ModalDataUpdateType,
} from '../../../types/order';

export interface ScheduleOffRentOrderModalProps {
  isOpen: OrdersModalVisibility;
  handleClose: () => void;
  offRentOrderDetails: ScheduleOffRentOrderDetails;
  updateActiveRentalsListHandler: (rentalId: number, orderData: ModalDataUpdateType) => void;
}

export function ScheduleOffRentOrderModal({
  isOpen,
  handleClose,
  offRentOrderDetails,
  updateActiveRentalsListHandler,
}: ScheduleOffRentOrderModalProps) {
  const { formatMessage } = useIntl();
  const authState = useAppSelector((state: RootState) => state.auth);
  const [isAlreadyScheduled, setIsAlreadyScheduled] = useState<boolean>(false);

  const closeModal = useCallback(() => {
    handleClose();
  }, [handleClose]);

  type FormFields = z.infer<typeof scheduleOffRentOrderUpdateSchema>;

  const formFields: FieldDetail[][] = [
    [
      {
        fieldType: OffRentOrderUpdateFormFieldType.PICKUP_DATE,
        fieldKey: 'pickupDate',
        fieldLabelId: 'myOrders.scheduleOffRentModal.pickUpDateLable',
        inputComponentType: InputFieldComponentType.DATE,
        inputType: 'date',
        inputMode: undefined,
        required: false,
        visible: true,
        disabled: false,
      },
    ],
    [
      {
        fieldType: OffRentOrderUpdateFormFieldType.NOTES,
        fieldKey: 'notes',
        fieldLabelId: 'myOrders.scheduleOffRentModal.notes',
        inputComponentType: InputFieldComponentType.TEXTAREA,
        inputType: 'text',
        inputMode: 'text',
        placeholderId: 'myOrders.scheduleOffRentModal.placeholders.notes',
        required: false,
        visible: true,
        disabled: false,
      },
    ],
  ];

  const formOnSubmit = async (data: { [key: string]: string }) => {
    try {
      const offRentOrderData: OffRentOrderData = {
        rental_id: offRentOrderDetails.rentalId,
        off_rent_date_requested: new Date(data.pickupDate).getTime().toString(),
      };

      // if already scheduled then first cancel previous schedule
      if (isAlreadyScheduled) {
        await cancelScheduleOffrent(offRentOrderDetails.rentalId);
      }

      if (data.notes) {
        const offRentOrderDataUpdateNotes: OffRentOrderDataUpdateNotes = {
          id: Number(offRentOrderDetails.rentalId),
          content: data.notes,
          rental_note_type_id: RENTAL_NOTES_TYPE_ID,
        };
        await offRentOrderNotesUpdate(offRentOrderDetails.rentalId, offRentOrderDataUpdateNotes);
      }

      await offRentOrder(
        authState.authContext?.companyId,
        offRentOrderDetails.rentalId,
        offRentOrderData,
      );

      offRentOrderData.off_rent_date_requested = data.pickupDate;
      updateActiveRentalsListHandler(Number(offRentOrderData.rental_id), offRentOrderData);
    } catch (error) {
      openMessageModal(messageContents.rentalModalErrorMessage);
      logger('error', 'at Schedule OffrentOrder:formOnSubmit', error);
      throw error;
    } finally {
      closeModal();
    }
  };

  const populateFormData = (data: ScheduleOffRentOrderDetails | null) => {
    if (!data) return null;

    const formData: FormFields = {
      pickupDate: data.pickupDate ?? new Date().toUTCString(),
      notes: data.notes ?? '',
    };

    return formData;
  };

  const getEquipmentHeaderDetails = () => {
    return (
      <>
        <EquipmentDetails bottomPadding={isMobileDevice()}>
          <EquipmentDetailsHeader>{offRentOrderDetails.equipmentName}</EquipmentDetailsHeader>
          <EquipmentDetailsSubHeader>{offRentOrderDetails.equipmentId}</EquipmentDetailsSubHeader>
        </EquipmentDetails>
        <LineBreak />
      </>
    );
  };

  useEffect(() => {
    setIsAlreadyScheduled(!!offRentOrderDetails.pickupDate);
  }, []);

  return (
    <>
      <UpdateModal
        open={isOpen === OrdersModalVisibility.CENTERED_MODAL}
        closeModal={closeModal}
        ariaLabel={`offrent-order-update-modal`}
        headerLabel={
          isAlreadyScheduled
            ? formatMessage({ id: 'myOrders.scheduleOffRentModal.modal.scheduleHeader' })
            : formatMessage({ id: 'myOrders.scheduleOffRentModal.modal.rescheduleHeader' })
        }
      >
        <UpdateFormContainer>
          {getEquipmentHeaderDetails()}
          <UpdateForm<FormFields>
            formData={populateFormData(offRentOrderDetails)}
            onSubmit={formOnSubmit}
            schema={scheduleOffRentOrderUpdateSchema}
            fieldDetails={formFields}
            submittingButtonLabelId={'myOrders.scheduleOffRentModal.savingButtonLabel'}
            submitButtonLabelId={'myOrders.scheduleOffRentModal.saveButtonLabel'}
          />
        </UpdateFormContainer>
      </UpdateModal>
      <BottomSheet isOpen={isOpen === OrdersModalVisibility.BOTTOM_SHEET} onClose={handleClose}>
        <BottomsheetContentWrapper>
          <BottomSheetTopBar>
            <BottomSheetLabel>
              {isAlreadyScheduled
                ? formatMessage({ id: 'myOrders.scheduleOffRentModal.modal.scheduleHeader' })
                : formatMessage({ id: 'myOrders.scheduleOffRentModal.modal.rescheduleHeader' })}
            </BottomSheetLabel>
            <CloseButton onClick={handleClose} cartModalType={OrderDisplayModalType.BOTTOM_SHEET}>
              <CloseIcon></CloseIcon>
            </CloseButton>
          </BottomSheetTopBar>
          <BottomsheetEquipmentContentWrapper>
            {getEquipmentHeaderDetails()}
          </BottomsheetEquipmentContentWrapper>
          <UpdateForm<FormFields>
            formData={populateFormData(offRentOrderDetails)}
            onSubmit={formOnSubmit}
            schema={scheduleOffRentOrderUpdateSchema}
            fieldDetails={formFields}
            submittingButtonLabelId={'myOrders.scheduleOffRentModal.savingButtonLabel'}
            submitButtonLabelId={'myOrders.scheduleOffRentModal.saveButtonLabel'}
          />
        </BottomsheetContentWrapper>
      </BottomSheet>
    </>
  );
}
